import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, KeyRound } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("Lien invalide ou expire.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    if (password.length < 8) { setError("8 caracteres minimum."); return; }
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password }, { withCredentials: true });
      await refresh();
      toast.success("Mot de passe mis a jour, vous etes connecte.");
      navigate("/mon-compte", { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Echec de la reinitialisation.";
      setError(typeof msg === "string" ? msg : "Echec de la reinitialisation.");
      toast.error(typeof msg === "string" ? msg : "Echec de la reinitialisation.");
    } finally { setLoading(false); }
  };

  return (
    <main className="max-w-md mx-auto px-6 py-16" data-testid="reset-password-page">
      <div className="glass-card p-8">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-400 text-white grid place-items-center mx-auto">
          <KeyRound size={22} />
        </div>
        <h1 className="font-display text-2xl font-extrabold text-center mt-4">Reinitialiser le mot de passe</h1>
        <p className="text-center text-sm text-[var(--ink-soft)] mt-1">Choisissez un nouveau mot de passe (8 caracteres minimum).</p>

        <form onSubmit={onSubmit} className="space-y-3 mt-6">
          <PasswordField value={password} onChange={setPassword} placeholder="Nouveau mot de passe" testId="reset-pw" />
          <PasswordField value={confirm} onChange={setConfirm} placeholder="Confirmer le mot de passe" testId="reset-pw-confirm" />
          {error && <p className="text-sm text-red-600 font-semibold" data-testid="reset-error">{error}</p>}
          <button
            type="submit"
            disabled={loading || !token}
            className="btn-primary w-full !py-3 inline-flex items-center justify-center gap-2"
            data-testid="reset-submit-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? "Validation..." : "Definir mon mot de passe"}
          </button>
        </form>

        <p className="text-center text-xs text-[var(--ink-soft)] mt-5">
          <Link to="/" className="underline">Retour a l&apos;accueil</Link>
        </p>
      </div>
    </main>
  );
}

function PasswordField({ value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]"><Lock size={16} /></span>
        <input
          type="password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required
          minLength={8}
          data-testid={testId}
          className="w-full !pl-10 !py-3 rounded-2xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition"
        />
      </div>
    </label>
  );
}
